/**
 * SyntheseCard — hero card at the top of the Analyse IA tab.
 *
 * Renders a 3-4 phrase executive summary of the fiche. Generated once by
 * `generateAiSynthese` when the tab first mounts ; re-generated silently
 * whenever the fiche's structural inputs change (the server-side hash
 * check lives in the callable, see functions/src/generateAiSynthese.ts).
 *
 * UX spec :
 *   - Full-width hero, generous leading, no visible controls.
 *   - Small meta row at the bottom : "Générée le X · claude-sonnet-4-5".
 *   - Skeleton while loading — no spinner, no "generating" label. Users
 *     don't need to know there's an LLM behind it ; they just need to read
 *     the sentence once it lands.
 *   - Error state : polite fallback, no red alert. Analyse IA is a "nice
 *     to have" layer and a broken synthèse shouldn't look like a crash.
 */

import { useAiSynthese } from "../../lib/ai/synthese";
import { Card } from "../ui";
import { Ic } from "../icons";

type Props = { ficheId: string };

export function SyntheseCard({ ficheId }: Props) {
  const state = useAiSynthese(ficheId);

  return (
    <Card className="p-6 md:p-7 bg-gradient-to-br from-lime-50 via-paper to-paper">
      <header className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-lime-200 grid place-items-center text-ink-900">
          <Ic.Sparkle size={20} />
        </div>
        <div>
          <h3 className="text-[11.5px] uppercase tracking-[0.12em] text-lime-700 font-medium">
            Analyse IA
          </h3>
          <h2 className="text-[18px] md:text-[19px] font-semibold tracking-tight text-ink-900">
            Synthèse exécutive
          </h2>
        </div>
      </header>

      <div className="mt-5">
        {state.status === "idle" || state.status === "loading" ? (
          <Skeleton />
        ) : state.status === "error" ? (
          <ErrorBox />
        ) : (
          <>
            <p className="text-[15px] md:text-[15.5px] leading-[1.65] text-ink-800">
              {state.data.content}
            </p>
            <Meta
              generatedAt={state.data.generatedAt}
              model={state.data.model}
            />
          </>
        )}
      </div>
    </Card>
  );
}

// ─── Meta row ────────────────────────────────────────────────────────────

function Meta({
  generatedAt,
  model,
}: {
  generatedAt: string;
  model: string;
}) {
  const date = formatDate(generatedAt);
  return (
    <div className="mt-5 pt-4 border-t border-ink-100 flex items-center gap-2 text-[11px] text-ink-500">
      <Ic.Sparkle size={11} />
      <span>
        Synthèse générée automatiquement{date ? ` le ${date}` : ""}
        {model ? ` · ${model}` : ""}. Mise à jour silencieuse si les données
        de la fiche évoluent.
      </span>
    </div>
  );
}

function formatDate(iso: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ─── Skeleton ────────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div className="space-y-2.5 animate-pulse" aria-hidden>
      <div className="h-4 rounded bg-ink-100 w-[95%]" />
      <div className="h-4 rounded bg-ink-100 w-[88%]" />
      <div className="h-4 rounded bg-ink-100 w-[92%]" />
      <div className="h-4 rounded bg-ink-100 w-[60%]" />
    </div>
  );
}

// ─── Error fallback ─────────────────────────────────────────────────────

function ErrorBox() {
  // Soft-fail : no red banner. The user can still use the Fiche and
  // Environnement tabs — the synthèse is a garnish, not the main course.
  return (
    <div className="rounded-lg bg-ink-50 px-4 py-3 text-[13px] text-ink-700 leading-relaxed">
      La synthèse IA est indisponible pour le moment. Les autres onglets
      restent entièrement consultables — réessayez dans quelques minutes.
    </div>
  );
}
